import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import CallbackModal from './CallbackModal';

// Emoji mapping helper for services
const getServiceEmoji = (name) => {
  const n = name.toLowerCase();
  if (n.includes('itr') || n.includes('income tax')) return '📋';
  if (n.includes('gst registration')) return '🧾';
  if (n.includes('gst return')) return '📊';
  if (n.includes('dsc') || n.includes('signature')) return '🔐';
  if (n.includes('tds')) return '📈';
  if (n.includes('msme') || n.includes('udyam')) return '🏢';
  if (n.includes('import export') || n.includes('ie code')) return '🌐';
  if (n.includes('company')) return '⚖️';
  if (n.includes('book keeping') || n.includes('accounting')) return '📚';
  if (n.includes('fssai') || n.includes('food')) return '🍏';
  if (n.includes('trademark')) return '🏷️';
  if (n.includes('fdi')) return '🇮🇳';
  if (n.includes('nri')) return '✈️';
  if (n.includes('iso')) return '🏅';
  if (n.includes('ngo') || n.includes('society')) return '🤝';
  return '📞';
};

export default function Navbar() {
  const { user, logout, isAdmin, isLoggedIn } = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const path         = location.pathname;
  const [showServices, setShowServices] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // Mobile menu open state
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false); // Collapsible mobile services accordion
  const [showProfileDropdown, setShowProfileDropdown] = useState(false); // Collapsible user profile dropdown
  const [activeServices, setActiveServices] = useState([]);
  const [selectedOfflineService, setSelectedOfflineService] = useState(null);
  const dropdownRef  = useRef(null);
  const profileDropdownRef = useRef(null);

  const handleLogout = () => { logout(); navigate('/'); };
  const initials = user?.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const active = (...paths) =>
    paths.some(p => path === p || path.startsWith(p)) ? 'nav-link active' : 'nav-link';

  useEffect(() => {
    // Fetch active services on mount
    api.get('/api/app-services/public')
      .then(res => setActiveServices(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowServices(false);
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target))
        setShowProfileDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Group services
  const onlineServices = activeServices.filter(s => s.type === 'ONLINE');
  const registrationServices = activeServices.filter(s => s.type === 'OFFLINE' && s.category === 'Registrations');
  const advisoryServices = activeServices.filter(s => s.type === 'OFFLINE' && s.category !== 'Registrations');

  const renderNavItems = (isMobile) => {
    return (
      <>
        {/* ── Public links ── */}
        {!isLoggedIn && (
          <>
            <Link to="/" className={active('/')} onClick={() => setIsOpen(false)}>Home</Link>
            <Link to="/updates" className={active('/updates')} onClick={() => setIsOpen(false)}>Updates & Files</Link>
            
            {/* Public Services Dropdown */}
            {isMobile ? (
              <>
                <button
                  type="button"
                  className="mobile-sub-link"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  style={{
                    width: '100%',
                    padding: '0.8rem 1.5rem',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  Our Services {mobileServicesOpen ? '▲' : '▼'}
                </button>
                {mobileServicesOpen && (
                  <div style={{
                    background: 'rgba(255,255,255,0.04)',
                    width: '100%',
                    borderRadius: '8px',
                    padding: '0.5rem 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.2rem',
                    maxHeight: '260px',
                    overflowY: 'auto'
                  }}>
                    {onlineServices.map(s => (
                      <Link key={s.id} to="/login" className="mobile-sub-link" onClick={() => setIsOpen(false)}>
                        {getServiceEmoji(s.name)} {s.name}
                      </Link>
                    ))}
                    {activeServices.filter(s => s.type === 'OFFLINE').map(s => (
                      <button key={s.id} className="mobile-sub-link-btn" onClick={() => { setIsOpen(false); setSelectedOfflineService(s); }}>
                        {getServiceEmoji(s.name)} {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  className={`nav-link-btn ${showServices ? 'open' : ''}`}
                  onClick={() => setShowServices(s => !s)}>
                  Services {showServices ? '▲' : '▼'}
                </button>
                {showServices && renderMegaMenu()}
              </div>
            )}
            
            <a href="/#callback" className="nav-link" onClick={() => setIsOpen(false)}>Contact</a>
          </>
        )}

        {/* ── User links ── */}
        {isLoggedIn && !isAdmin && (
          <>
            <Link to="/dashboard" className={active('/dashboard')} onClick={() => setIsOpen(false)}>Dashboard</Link>
            <Link to="/updates" className={active('/updates')} onClick={() => setIsOpen(false)}>Updates & Files</Link>
            
            {isMobile ? (
              <>
                <button
                  type="button"
                  className="mobile-sub-link"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  style={{
                    width: '100%',
                    padding: '0.8rem 1.5rem',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  Our Services {mobileServicesOpen ? '▲' : '▼'}
                </button>
                {mobileServicesOpen && (
                  <div style={{
                    background: 'rgba(255,255,255,0.04)',
                    width: '100%',
                    borderRadius: '8px',
                    padding: '0.5rem 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.2rem',
                    maxHeight: '260px',
                    overflowY: 'auto'
                  }}>
                    {onlineServices.map(s => (
                      <Link key={s.id} to={s.link || '/dashboard'} className="mobile-sub-link" onClick={() => setIsOpen(false)}>
                        {getServiceEmoji(s.name)} {s.name}
                      </Link>
                    ))}
                    {activeServices.filter(s => s.type === 'OFFLINE').map(s => (
                      <button key={s.id} className="mobile-sub-link-btn" onClick={() => { setIsOpen(false); setSelectedOfflineService(s); }}>
                        {getServiceEmoji(s.name)} {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  className={`nav-link-btn ${showServices ? 'open' : ''}`}
                  onClick={() => setShowServices(s => !s)}>
                  Services {showServices ? '▲' : '▼'}
                </button>
                {showServices && renderMegaMenu()}
              </div>
            )}
          </>
        )}

        {/* ── Admin links ── */}
        {isAdmin && (
          <>
            <Link to="/admin" className={active('/admin')} onClick={() => setIsOpen(false)}>Dashboard</Link>
            <Link to="/admin/filings" className={active('/admin/filings')} onClick={() => setIsOpen(false)}>ITR</Link>
            <Link to="/admin/gst" className={active('/admin/gst')} onClick={() => setIsOpen(false)}>GST Reg.</Link>
            <Link to="/admin/gst-returns" className={active('/admin/gst-returns')} onClick={() => setIsOpen(false)}>GST Returns</Link>
            <Link to="/admin/dsc" className={active('/admin/dsc')} onClick={() => setIsOpen(false)}>DSC</Link>
            <Link to="/admin/payments" className={active('/admin/payments')} onClick={() => setIsOpen(false)}>💳 Payments</Link>
            <Link to="/admin/enquiries" className={active('/admin/enquiries')} onClick={() => setIsOpen(false)}>Enquiries</Link>
          </>
        )}

        {/* ── Auth / Profile ── */}
        {isLoggedIn ? (
          isMobile ? (
            <div className="nav-user-mobile" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-start', marginTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem', width: '100%', paddingLeft: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div className="nav-avatar">{initials}</div>
                <span style={{ color: '#fff', fontWeight: 600 }}>{user.fullName?.split(' ')[0]}</span>
                {isAdmin && (
                  <span style={{ fontSize: '.7rem', background: 'rgba(201,146,42,.2)', color: 'var(--goldt)', padding: '.15rem .5rem', borderRadius: 4 }}>
                    ADMIN
                  </span>
                )}
              </div>
              {isAdmin && (
                <Link to="/admin/settings" className="nav-link" style={{ textAlign: 'left', width: '100%', padding: '0.8rem 0' }} onClick={() => setIsOpen(false)}>
                  ⚙️ Settings
                </Link>
              )}
              <button className="nav-btn" style={{ alignSelf: 'flex-start' }} onClick={() => { setIsOpen(false); handleLogout(); }}>Logout</button>
            </div>
          ) : (
            <div ref={profileDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="nav-link-btn"
                onClick={() => setShowProfileDropdown(p => !p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#fff',
                  padding: '0.4rem 0.6rem',
                  borderRadius: '8px',
                  fontFamily: 'inherit'
                }}
              >
                <div className="nav-avatar" style={{ margin: 0 }}>{initials}</div>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.fullName?.split(' ')[0]}</span>
                <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{showProfileDropdown ? '▲' : '▼'}</span>
              </button>
              
              {showProfileDropdown && (
                <div
                  className="profile-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'var(--navyd)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '0.6rem 0',
                    minWidth: '180px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                    zIndex: 110,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ padding: '0.4rem 1rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.4rem' }}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.fullName}</div>
                    <div style={{ color: 'var(--goldt)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' }}>
                      {isAdmin ? 'Admin' : 'User'}
                    </div>
                  </div>

                  {isAdmin && (
                    <Link
                      to="/admin/settings"
                      className="profile-dropdown-item"
                      onClick={() => setShowProfileDropdown(false)}
                      style={{
                        padding: '0.5rem 1rem',
                        color: 'rgba(255,255,255,0.8)',
                        textDecoration: 'none',
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        textAlign: 'left'
                      }}
                    >
                      ⚙️ Settings
                    </Link>
                  )}

                  <button
                    onClick={() => { setShowProfileDropdown(false); handleLogout(); }}
                    className="profile-dropdown-item"
                    style={{
                      padding: '0.5rem 1rem',
                      color: '#f87171',
                      background: 'none',
                      border: 'none',
                      fontSize: '0.82rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      fontFamily: 'inherit'
                    }}
                  >
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          )
        ) : (
          <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%', marginTop: '1.5rem', paddingLeft: '1.5rem' } : { display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Link to="/login" className="nav-link" style={isMobile ? { textAlign: 'left', padding: '0.8rem 0' } : {}} onClick={() => setIsOpen(false)}>Login</Link>
            <Link to="/register" style={isMobile ? { width: 'auto' } : {}} onClick={() => setIsOpen(false)}>
              <button className="nav-btn" style={isMobile ? { width: 'auto' } : {}}>Register Free</button>
            </Link>
          </div>
        )}
      </>
    );
  };

  const renderMegaMenu = () => {
    return (
      <div className="mega-dropdown-menu">
        
        {/* Column 1: Online Services */}
        <div className="mega-col">
          <div className="mega-col-title">🖥️ Online Filings</div>
          {onlineServices.map(s => (
            <Link
              key={s.id}
              to={isLoggedIn ? (s.link || '/dashboard') : '/login'}
              className="mega-item"
              onClick={() => setShowServices(false)}
            >
              <span className="mega-icon">{getServiceEmoji(s.name)}</span>
              <div>
                <div className="mega-label">{s.name}</div>
                <div className="mega-sub">{s.description || 'Fast online processing'}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Column 2: Registrations */}
        <div className="mega-col">
          <div className="mega-col-title">🏢 Registrations & Licenses</div>
          {registrationServices.map(s => (
            <button
              key={s.id}
              className="mega-item-btn"
              onClick={() => { setShowServices(false); setSelectedOfflineService(s); }}
            >
              <span className="mega-icon">{getServiceEmoji(s.name)}</span>
              <div>
                <div className="mega-label">{s.name}</div>
                <div className="mega-sub">{s.description || 'Get registered quickly'}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Column 3: Business & Legal Advisory */}
        <div className="mega-col">
          <div className="mega-col-title">💼 Business & Legal Advisory</div>
          {advisoryServices.map(s => (
            <button
              key={s.id}
              className="mega-item-btn"
              onClick={() => { setShowServices(false); setSelectedOfflineService(s); }}
            >
              <span className="mega-icon">{getServiceEmoji(s.name)}</span>
              <div>
                <div className="mega-label">{s.name}</div>
                <div className="mega-sub">{s.description || 'Expert consultation'}</div>
              </div>
            </button>
          ))}
        </div>

      </div>
    );
  };

  return (
    <>
      <style>{`
        .mega-dropdown-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: -120px;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: 0 15px 40px rgba(7,15,30,0.18);
          display: grid;
          grid-template-columns: repeat(3, 260px);
          gap: 1.5rem;
          padding: 1.5rem;
          z-index: 999;
          animation: megaFadeDown 0.2s ease-out;
        }
        @keyframes megaFadeDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mega-col {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .mega-col-title {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--gold);
          font-weight: 700;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid var(--creamd);
          margin-bottom: 0.25rem;
        }
        .mega-item, .mega-item-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.88rem;
          color: var(--text);
          text-decoration: none;
          background: none;
          border: none;
          text-align: left;
          border-radius: 8px;
          transition: all 0.15s;
          cursor: pointer;
          width: 100%;
        }
        .mega-item:hover, .mega-item-btn:hover {
          background: var(--cream);
          color: var(--navy);
          transform: translateX(2px);
        }
        .mega-item .mega-icon, .mega-item-btn .mega-icon {
          font-size: 1.1rem;
          width: 24px;
          text-align: center;
          flex-shrink: 0;
        }
        .mega-item .mega-label, .mega-item-btn .mega-label {
          font-weight: 600;
          font-size: 0.83rem;
          color: var(--navy);
        }
        .mega-item .mega-sub, .mega-item-btn .mega-sub {
          font-size: 0.68rem;
          color: var(--muted);
          margin-top: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 190px;
        }
        .nav-link-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,.7);
          font-size: .88rem;
          font-weight: 500;
          padding: .5rem .9rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all .2s;
          display: flex;
          align-items: center;
          gap: .3rem;
          font-family: 'DM Sans', sans-serif;
        }
        .nav-link-btn:hover { background: rgba(255,255,255,.08); color: #fff; }
        .nav-link-btn.open  { color: var(--goldt); }
        .profile-dropdown-item {
          padding: 0.5rem 1rem;
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-align: left;
          background: none;
          border: none;
          width: 100%;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s;
        }
        .profile-dropdown-item:hover {
          background: rgba(255,255,255,0.06);
          color: #fff !important;
        }
        .hamburger-btn {
          display: none;
          background: none;
          border: none;
          color: #fff;
          font-size: 1.8rem;
          cursor: pointer;
          z-index: 220;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 6px;
          transition: background-color 0.2s;
          outline: none;
        }
        .hamburger-btn:hover {
          background: rgba(255, 255, 255, 0.08);
        }
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: var(--navy);
          z-index: 215;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 80px 1.5rem 2rem;
          overflow-y: auto;
          animation: slideIn 0.25s ease-out;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mobile-menu-overlay .nav-link {
          font-size: 1.1rem;
          padding: 0.8rem 1.5rem;
          width: 100%;
          text-align: left;
          display: block;
        }
        .mobile-section-title {
          padding: 0.75rem 1.5rem 0.25rem;
          color: var(--goldt);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-weight: 700;
          text-align: left;
          margin-top: 0.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          width: 100%;
        }
        .mobile-sub-link, .mobile-sub-link-btn {
          padding: 0.6rem 2.5rem;
          color: rgba(255,255,255,0.7);
          font-size: 0.9rem;
          text-decoration: none;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          display: block;
        }
        .mobile-sub-link:hover, .mobile-sub-link-btn:hover {
          color: #fff;
          background: rgba(255,255,255,0.05);
        }
        @media (max-width: 768px) {
          .nav-links { display: none; }
          .hamburger-btn { display: flex; }
        }
      `}</style>

      <nav className="nav">
        {/* ── Logo ── */}
        <Link to="/" className="nav-logo" onClick={() => setIsOpen(false)}>
          <div className="nav-icon"
            style={{ fontFamily: 'Playfair Display, serif', fontSize: '.85rem', fontWeight: 700 }}>T</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '.05rem' }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '.65rem', color: 'rgba(255,255,255,.4)', letterSpacing: '.2em', fontWeight: 400 }}>MY</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', color: '#fff', fontWeight: 700 }}>Tax</span>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', color: 'var(--goldt)', fontWeight: 700 }}>नीति</span>
            </div>
          </div>
        </Link>

        {/* Desktop nav links */}
        <div className="nav-links">
          {renderNavItems(false)}
        </div>

        {/* Hamburger toggle button */}
        <button 
          className="hamburger-btn" 
          onClick={() => { setIsOpen(!isOpen); setMobileServicesOpen(false); }}
          aria-label="Toggle navigation menu"
        >
          {isOpen ? '✕' : '☰'}
        </button>

        {/* Mobile menu overlay */}
        {isOpen && (
          <div className="mobile-menu-overlay">
            {renderNavItems(true)}
          </div>
        )}
      </nav>

      {/* Callback Modal */}
      {selectedOfflineService && (
        <CallbackModal
          service={selectedOfflineService}
          onClose={() => setSelectedOfflineService(null)}
        />
      )}
    </>
  );
}